import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock } from "lucide-react";
import blogPosts from "@/data/blogPosts";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

export default function BlogPage() {
  return (
    <div className="min-h-screen bg-white">
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-3 h-3 bg-black rounded-full" />
            <span className="text-lg font-semibold tracking-tight">
              <span className="text-gray-800">SHADOW</span>
              <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
                LABS
              </span>
            </span>
          </Link>
          <Link
            to="/"
            className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
          >
            ← Back to home
          </Link>
        </nav>
      </header>

      <main className="pt-28 pb-24 px-4 sm:px-8">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="mb-14"
          >
            <p className="text-xs tracking-widest uppercase text-teal-600 font-medium mb-3">
              Blog
            </p>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-gray-900 leading-tight">
              Thinking out loud.
            </h1>
            <p className="mt-4 text-gray-500 text-base sm:text-lg leading-relaxed max-w-xl">
              Ideas on conversation intelligence, high-stakes decision-making, and what AI actually changes.
            </p>
          </motion.div>

          <div className="space-y-8">
            {blogPosts.map((post, i) => (
              <motion.div
                key={post.slug}
                custom={i + 1}
                initial="hidden"
                animate="visible"
                variants={fadeUp}
              >
                <Link
                  to={`/blog/${post.slug}`}
                  className="group block bg-white border border-gray-100 rounded-2xl p-7 sm:p-9 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-xs text-gray-400">{post.date}</span>
                    <span className="w-1 h-1 rounded-full bg-gray-300" />
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                      <Clock size={11} />
                      {post.readTime}
                    </span>
                    <span className="w-1 h-1 rounded-full bg-gray-300" />
                    <span className="text-xs text-teal-600 font-medium">
                      {post.persona}
                    </span>
                  </div>

                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 leading-snug mb-3 group-hover:text-teal-700 transition-colors duration-200">
                    {post.title}
                  </h2>

                  <p className="text-gray-500 text-sm sm:text-base leading-relaxed mb-5">
                    {post.excerpt}
                  </p>

                  <div className="flex items-center gap-1.5 text-sm font-medium text-gray-900 group-hover:text-teal-600 transition-colors duration-200">
                    Read post
                    <ArrowRight
                      size={14}
                      className="group-hover:translate-x-1 transition-transform duration-200"
                    />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </main>

      <footer className="border-t border-gray-100 py-10 px-4 sm:px-8">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-2.5 h-2.5 bg-black rounded-full" />
            <span className="text-sm font-semibold tracking-tight">
              <span className="text-gray-800">SHADOW</span>
              <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
                LABS
              </span>
            </span>
          </Link>
          <p className="text-xs text-gray-400">
            &copy; 2026 ShadowLabs. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
